import React, { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { U2000AutoCompleteSearchCountFooter } from '../../../u2000Core/components/common/U2000AutoCompleteSearchCountFooter';
import type { U3129FindProjectSortField, U3129FindProjectSortOrder } from '../../tools/u3129BuildFindProjectSearchCriterias';
import { U3129ProjectSearchSortableHeader } from './U3129ProjectSearchSortableHeader';

type GenericObject = Record<string, unknown>;

export interface U3129ProjectSearchResultsTableProps {
  results: GenericObject[];
  resultFields: string[];
  handleRowClick: (item: GenericObject, idx: number) => void;
  showResults: boolean;
  setShowResults: (show: boolean) => void;
  highlightedIndex: number;
  setHighlightedIndex: Dispatch<SetStateAction<number>>;
  className?: string;
  totalCount?: number;
  ns: string;
  activeSortField: U3129FindProjectSortField;
  activeSortOrder: U3129FindProjectSortOrder;
  onSort: (field: U3129FindProjectSortField) => void;
}

/** Tableau résultats panneau projet U3129 avec en-têtes triables (BL-0598). */
export const U3129ProjectSearchResultsTable: React.FC<U3129ProjectSearchResultsTableProps> = ({
  results,
  resultFields,
  handleRowClick,
  showResults,
  setShowResults,
  highlightedIndex,
  setHighlightedIndex,
  className,
  totalCount,
  ns,
  activeSortField,
  activeSortOrder,
  onSort,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  const resultsRef = useRef<GenericObject[]>(results);

  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showResults || resultsRef.current.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) {
            return resultsRef.current.length > 0 ? 0 : prev;
          }
          return prev < resultsRef.current.length - 1 ? prev + 1 : prev;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev <= 0 ? -1 : prev - 1));
      } else if (e.key === 'Enter') {
        setHighlightedIndex((prev) => {
          if (prev >= 0 && prev < resultsRef.current.length) {
            e.preventDefault();
            handleRowClick(resultsRef.current[prev], prev);
            setShowResults(false);
          }
          return prev;
        });
      } else if (e.key === 'Escape') {
        setShowResults(false);
        setHighlightedIndex(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown as EventListener);
    return () => window.removeEventListener('keydown', handleKeyDown as EventListener);
  }, [showResults, handleRowClick, setShowResults, setHighlightedIndex]);

  useEffect(() => {
    if (highlightedIndex >= 0 && rowRefs.current[highlightedIndex] && tableContainerRef.current) {
      const row = rowRefs.current[highlightedIndex];
      const container = tableContainerRef.current;
      if (row && container) {
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        if (rowTop < containerScrollTop) {
          container.scrollTop = rowTop;
        } else if (rowBottom > containerScrollTop + containerHeight) {
          container.scrollTop = rowBottom - containerHeight;
        }
      }
    }
  }, [highlightedIndex, results]);

  return (
    <div
      className={`mt-1 flex min-h-0 flex-col rounded-lg border surface-border bg-surface shadow-xl animate-fade-in ${className ?? ''}`}
      tabIndex={-1}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('thead')) return;
        e.preventDefault();
      }}
    >
      <div role="listbox" className="min-h-0 flex-1 overflow-y-auto" ref={tableContainerRef}>
        <table className="min-w-full text-sm">
          <U3129ProjectSearchSortableHeader
            ns={ns}
            activeField={activeSortField}
            activeOrder={activeSortOrder}
            onSort={onSort}
          />
          <tbody>
            {results.map((item, idx) => (
              <tr
                key={idx}
                ref={(el) => {
                  rowRefs.current[idx] = el;
                }}
                role="option"
                aria-selected={idx === highlightedIndex}
                className={`cursor-pointer transition-colors duration-200 ${idx === highlightedIndex ? 'bg-blue-50 text-blue-900' : 'hover:bg-muted text-gray-900'} ${
                  idx < results.length - 1 ? 'border-b border-gray-100' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleRowClick(item, idx);
                  setShowResults(false);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {resultFields.map((field, colIdx) => (
                  <td key={colIdx} className="px-3 py-2.5 text-sm">
                    {String(item[field] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <U2000AutoCompleteSearchCountFooter displayedCount={results.length} totalCount={totalCount} />
    </div>
  );
};
